import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Users, Search, PlusCircle, Filter, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import WizardShell from '../../../components/ui/wizard-shell';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { personListMeta } from '../../../lib/api/core';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import SubProfileForm from '../../../components/person/SubProfileForm';
import { useStandort } from '../../../components/layouts/use-standort';
import { useGeoapifyAddressAutocomplete } from '../../../hooks/useGeoapifyAddressAutocomplete';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import PersonGridCard from '../../../components/person/PersonGridCard';
import PersonDetailsWizardModal from '../../../components/person/PersonDetailsWizardModal';
import MessageWizardModal, { type MessageRecipient } from '../../../components/communications/MessageWizardModal';
import { personDetailPath, subprofileDetailPath } from '../../../lib/peopleRoutes';
import { relationshipText } from '../../../lib/subprofileRelationships';

type PersonListType = 'student' | 'customer' | 'teacher';
type AssignedProfile = {
  subprofileId: string;
  index: number;
  name: string;
  relationshipType?: string | null;
  birthDate?: string | null;
  aktiv?: boolean;
};
/** Sortierungen, die das Backend kennt (``GET /api/personen?sort=``). */
type PeopleSort = 'standard' | 'name_asc' | 'name_desc' | 'status' | 'activity_desc';
// Typen für Personen
interface Person {
  id: string;
  name: string;
  email: string;
  phone?: string;
  communicationChannels?: { type: string; value: string; officeContactAllowed?: boolean; isActive?: boolean }[];
  type: 'student' | 'teacher' | 'customer' | 'both';
  status: 'active' | 'inactive' | 'pending';
  instrument?: string;
  joinDate: string;
  birthDate?: string;
  address?: string;
  notes?: string;
  wpUserId?: string; // Übergangs-ID des früheren Kundenkontos
  parentId?: string; // ID of the parent/customer if this is a sub-profile
  parentName?: string;
  subprofileIndex?: number;
  isSubprofile?: boolean;
  relationshipType?: string;
  relationshipText?: string;
  lastHistoryType?: string | null;
  lastHistoryDescription?: string | null;
  lastHistoryAt?: string | null;
  subProfiles?: string[];
  roles?: string[]; // Importierte Rollen des früheren Kundenkontos
  isTeacher?: boolean;
  // Kontexte mit laufendem Vertrag bzw. Auftrag — woran „aktiv" haengt.
  activeContexts?: PersonListType[];
  computedTypes?: PersonListType[];
  /** Kunden-Reiter: die Lernenden des Kontos, vom Server mitgeliefert (T-0042). */
  assignedProfiles?: AssignedProfile[];
  parentWpUserId?: string;
  isPrimaryProfile?: boolean;
}
type SubprofileRecord = Record<string, unknown>;
type StructuredAddress = Record<string, unknown> & {
  formatted?: string;
  zip?: string;
  postalCode?: string;
  street?: string;
  houseNumber?: string;
  city?: string;
};
type CreateSalutation = '' | 'Herr' | 'Frau' | 'Divers';

// Scope, Suche, Status und Seite werden im Backend ausgewertet; die Liste zeigt
// eine Zeile je Profil, genau so, wie sie vom Server kommt.
const SCOPE_PARAM: Record<'all' | PersonListType, string> = {
  all: 'alle',
  student: 'schueler',
  customer: 'kunde',
  teacher: 'lehrer',
};
const STATUS_PARAM: Record<string, string> = {
  all: 'alle',
  active: 'aktiv',
  inactive: 'inaktiv',
  archived: 'archiviert',
};
const STATUS_LABEL: Record<string, string> = {
  active: 'Aktiv',
  inactive: 'Inaktiv',
  archived: 'Archiviert',
};
const KONTAKTWEG_LABEL: Record<string, string> = {
  email: 'E-Mail',
  sms: 'SMS',
  mobil: 'Mobil',
  festnetz: 'Festnetz',
};

const PersonManagement = () => {
  const { selectedStandortId } = useStandort();
  // Wie in der Kopfzeilen-Suche: normalerweise nur der gewählte Standort, auf Wunsch alle.
  const [alleStandorte, setAlleStandorte] = useState(false);
  const [ohneStandort, setOhneStandort] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [people, setPeople] = useState<Person[]>([]);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  // Die Suche laeuft im Backend; getippt wird schneller als abgefragt.
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const [activeTab, setActiveTab] = useState<'all' | PersonListType>('all');
  // Vorbelegt auf die laufenden Faelle; auf die ausgeblendeten weist die Liste hin.
  // Vorbelegt mit allen Status: Eine neu angelegte Person ohne Vertrag gilt als
  // inaktiv und war unter „Aktiv“ nicht zu finden (T-0264).
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState<PeopleSort>('standard');
  // Wie viele Treffer es ohne den Statusfilter gaebe.
  const [totalAllStatus, setTotalAllStatus] = useState(0);
  // Hochzaehlen laedt die Seite neu, etwa nachdem eine Person angelegt wurde.
  const [neuLaden, setNeuLaden] = useState(0);
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(0);
  const [subprofilesByParent, setSubprofilesByParent] = useState<Record<string, SubprofileRecord[]>>({});
  const [expandedCustomerIds, setExpandedCustomerIds] = useState<Record<string, boolean>>({});
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [isSubProfileDialogOpen, setIsSubProfileDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  // T-0175: Die E-Mail gehört schon jemandem — Hinweis mit Link, Anlegen nur nach Bestätigung.
  const [emailDublette, setEmailDublette] = useState<{ personId: string; name: string } | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsPersonId, setDetailsPersonId] = useState<string | null>(null);
  const [jumpToPage, setJumpToPage] = useState('');

  const [messageOpen, setMessageOpen] = useState(false);
  const [messageRecipients, setMessageRecipients] = useState<MessageRecipient[]>([]);
  const [createForm, setCreateForm] = useState({
    salutation: '' as CreateSalutation,
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    sendWelcomeEmail: false,
    makeTeacher: false,
    newsletter: false,
    birthDate: '',
    address: '',
    structuredAddress: undefined as StructuredAddress | undefined,
    communications: [] as { type: string; value: string }[],
    isCompany: false,
    companyName: '',
  });
  const [addressCreateQuery, setAddressCreateQuery] = useState('');
  const [addressCreateSuggestions, setAddressCreateSuggestions] = useState<SubprofileRecord[]>([]);

  const zeileAusApi = useMemo(
    () =>
      (row: SubprofileRecord, scope: 'all' | PersonListType): Person => {
        const index = Number(row?.subIndex || 1);
        const istKonto = index === 1;
        const kontoId = String(row?.parentId ?? row?.person_id ?? '');
        const isTeacher = row?.isTeacher === true || (row?.roles as string[] | undefined || []).includes('teacher');
        const kanaele = (Array.isArray(row?.communicationChannels)
          ? row.communicationChannels
          : []) as Person['communicationChannels'];
        const eigeneNummer = (kanaele || []).find(
          (kanal) => ['telefon', 'mobil', 'sms', 'festnetz'].includes(kanal?.type)
            && Boolean(kanal?.value)
        )?.value;
        // Die Plaketten sagen, was die Zeile ist. In einem Reiter nur dessen Rolle —
        // „(Kunde) (Lehrer)“ im Kunden-Reiter lag außerhalb des Gesuchten (T-0042/43).
        // Unter „Alle“ bleiben alle Rollen sichtbar.
        const computedTypes: PersonListType[] = scope !== 'all'
          ? [scope]
          : istKonto
            ? ['customer' as PersonListType, ...(isTeacher ? ['teacher' as PersonListType] : [])]
            : ['student'];
        const kontoName = String(row?.parentName || '');

        return {
          id: String(row?.id || `${kontoId}-${index}`),
          name: String(row?.name || `${row?.first_name || ''} ${row?.last_name || ''}`.trim() || `Profil ${index}`),
          email: String(row?.email || ''),
          phone: String(eigeneNummer || row?.phone || ''),
          communicationChannels: kanaele,
          type: istKonto ? (isTeacher ? 'teacher' : 'customer') : 'student',
          status: row?.is_active === false ? 'inactive' : 'active',
          joinDate: '',
          birthDate: (row?.birthDate as string) || undefined,
          wpUserId: kontoId,
          parentId: istKonto ? undefined : kontoId,
          parentName: istKonto ? undefined : kontoName,
          isSubprofile: !istKonto,
          isPrimaryProfile: istKonto,
          subprofileIndex: index,
          relationshipType: (row?.relationshipType as string) || undefined,
          relationshipText: !istKonto
            ? (relationshipText((row?.relationshipType as string) || null, kontoName) || undefined)
            : undefined,
          lastHistoryType: (row?.lastHistoryType as string) ?? null,
          lastHistoryDescription: (row?.lastHistoryDescription as string) ?? null,
          lastHistoryAt: (row?.lastHistoryAt as string) ?? null,
          roles: (row?.roles as string[]) || [],
          isTeacher,
          activeContexts: (row?.activeContexts as PersonListType[]) || [],
          computedTypes,
          assignedProfiles: Array.isArray(row?.assignedProfiles)
            ? (row.assignedProfiles as AssignedProfile[])
            : undefined,
          parentWpUserId: kontoId,
          subProfiles: Array.from(
            { length: Number(row?.subprofileCount || 0) },
            (_wert, position) => `${kontoId}-${position + 2}`
          ),
        };
      },
    []
  );

  useEffect(() => {
    let abgebrochen = false;
    const laden = async () => {
      if (!selectedStandortId && !alleStandorte) {
        setPeople([]);
        setTotalFiltered(0);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('scope', SCOPE_PARAM[activeTab]);
        params.set('status', STATUS_PARAM[statusFilter] || 'alle');
        params.set('sort', sortBy);
        if (alleStandorte) params.set('all_standorte', '1');
        else params.set('standort_id', String(selectedStandortId));
        if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim());
        params.set('limit', String(PAGE_SIZE));
        params.set('offset', String(page * PAGE_SIZE));

        const rows = await api.get<SubprofileRecord[]>(`/api/personen?${params.toString()}`);
        if (abgebrochen) return;
        const liste = Array.isArray(rows) ? rows : [];
        setPeople(liste.map((row) => zeileAusApi(row, activeTab)));
        setTotalFiltered(personListMeta(rows).total);
        setTotalAllStatus(personListMeta(rows).totalAllStatus);
        setOhneStandort(personListMeta(rows).withoutStandort);
      } catch (error) {
        if (abgebrochen) return;
        console.error('Fehler beim Laden der Personen:', error);
        toast.error('Personen konnten nicht geladen werden');
        setPeople([]);
        setTotalFiltered(0);
        setTotalAllStatus(0);
      } finally {
        if (!abgebrochen) setIsLoading(false);
      }
    };
    laden();
    return () => {
      abgebrochen = true;
    };
  }, [
    selectedStandortId,
    alleStandorte,
    activeTab,
    statusFilter,
    sortBy,
    debouncedSearch,
    page,
    neuLaden,
    zeileAusApi,
  ]);

  const { suggestions: addressCreateSuggestionsHook } = useGeoapifyAddressAutocomplete({
    query: addressCreateQuery,
    enabled: isCreateDialogOpen,
  });

  useEffect(() => {
    setAddressCreateSuggestions(addressCreateSuggestionsHook as SubprofileRecord[]);
  }, [addressCreateSuggestionsHook]);

  // Create person
  const handleCreatePerson = async (trotzdemAnlegen = false) => {
    if (!createForm.email || (!createForm.firstName && !createForm.lastName)) {
      toast.error('Bitte E-Mail und mindestens Vor- oder Nachname angeben');
      return;
    }
    setIsLoading(true);
    try {
      if (!trotzdemAnlegen) {
        const pruefung = await api.get<{ vergeben: boolean; personId?: string; name?: string }>(
          `/api/personen/email-vergeben?email=${encodeURIComponent(createForm.email.trim())}`,
        );
        if (pruefung?.vergeben && pruefung.personId) {
          setEmailDublette({ personId: String(pruefung.personId), name: pruefung.name || 'einer anderen Person' });
          return;
        }
      }
      const payload: Record<string, unknown> = {
        email: createForm.email,
        first_name: createForm.firstName,
        last_name: createForm.lastName,
        salutation: createForm.salutation || undefined,
        newsletter: createForm.newsletter || undefined,
        birthDate: createForm.birthDate || undefined,
        communicationChannels: createForm.communications || undefined,
      };
      if (createForm.isCompany && createForm.companyName) {
        payload.companyName = createForm.companyName;
      }
      if (createForm.structuredAddress) {
        const zip = String(createForm.structuredAddress?.zip || createForm.structuredAddress?.postalCode || '').trim();
        payload.structuredAddress = {
          ...createForm.structuredAddress,
          zip,
          postalCode: zip,
        };
      }
      else if (createForm.address) payload.address = createForm.address;
      if (trotzdemAnlegen) payload.trotzdemAnlegen = true;
      const created = await api.post<Person>('/api/personen', payload);
      if (selectedStandortId) {
        try {
          await api.put(`/api/personen/${created.id}/standorte`, {
            standort_ids: [selectedStandortId],
          });
        } catch (standortError) {
          console.error('Standortzuordnung fehlgeschlagen:', standortError);
          toast.warning('Person wurde angelegt, aber der Standort konnte nicht zugeordnet werden.');
        }
      }
      // Die Liste kommt vom Server; die neue Person erscheint beim naechsten Laden.
      setNeuLaden((wert) => wert + 1);
      toast.success('Person wurde erstellt');
      setIsCreateDialogOpen(false);
      setEmailDublette(null);
      setCreateForm({ salutation: '', firstName: '', lastName: '', email: '', password: '', sendWelcomeEmail: false, makeTeacher: false, newsletter: false, birthDate: '', address: '', structuredAddress: undefined, communications: [], isCompany: false, companyName: '' });
      setAddressCreateQuery('');
      setAddressCreateSuggestions([]);
    } catch (e: unknown) {
      console.error('Person anlegen fehlgeschlagen:', e);
      toast.error(e instanceof Error ? e.message : 'Person konnte nicht erstellt werden');
    } finally {
      setIsLoading(false);
    }
  };
  const getRootPersonId = (person: Person) => String(person.parentWpUserId || person.parentId || person.id);
  const resolvePreferredPhone = (person: Person): string | null => {
    const channels = Array.isArray(person.communicationChannels) ? person.communicationChannels : [];
    const preferred = channels.find((c) =>
      (c.type === 'sms' || c.type === 'mobil' || c.type === 'festnetz')
      && c.value
      && c.officeContactAllowed !== false
    );
    return preferred?.value || person.phone || null;
  };
  const getLoadedSubprofiles = (person: Person) => subprofilesByParent[getRootPersonId(person)] || [];
  const getSubprofileCount = (person: Person) => {
    if (!person.isPrimaryProfile) return 0;
    const rootId = getRootPersonId(person);
    // Geladene Profile schlagen die Zahl vom Server — sie ist nach dem Anlegen aktueller.
    if (subprofilesByParent[rootId] !== undefined) return getLoadedSubprofiles(person).length;
    return (person.subProfiles || []).length;
  };
  const pagedPeople = people;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const pageStartIdx = page * PAGE_SIZE;
  const pageEndIdx = Math.min(pageStartIdx + people.length, totalFiltered);
  const hasActiveFilters = searchTerm.trim().length > 0 || activeTab !== 'all' || statusFilter !== 'all';
  // Wie viele Treffer die Vorbelegung „Aktiv" gerade zurueckhaelt.
  const ausgeblendeteTreffer = Math.max(0, totalAllStatus - totalFiltered);

  // Filterwechsel fuehrt zurueck auf Seite 1; sonst stuende man auf einer Seite,
  // die es im neuen Ergebnis nicht mehr gibt.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, activeTab, statusFilter, sortBy, selectedStandortId, alleStandorte]);

  // Ist die aktuelle Seite hinter das Ergebnis gerutscht, auf die letzte springen.
  useEffect(() => {
    if (totalFiltered === 0 || pageStartIdx < totalFiltered) return;
    const letzteSeite = Math.max(0, Math.ceil(totalFiltered / PAGE_SIZE) - 1);
    if (page !== letzteSeite) setPage(letzteSeite);
  }, [totalFiltered, pageStartIdx, page]);
  const clearFilters = () => {
    setSearchTerm('');
    setActiveTab('all');
    setStatusFilter('all');
    setSortBy('standard');
  };

  const goToPage = () => {
    const raw = Number.parseInt(jumpToPage, 10);
    if (!raw || Number.isNaN(raw)) return;
    const clamped = Math.min(Math.max(raw, 1), totalPages);
    setPage(clamped - 1);
    setJumpToPage('');
  };

  const compactPageButtons = (() => {
    const pages = new Set<number>([0, 1, totalPages - 2, totalPages - 1, page]);
    const sorted = Array.from(pages).filter((p) => p >= 0 && p < totalPages).sort((a, b) => a - b);
    const items: Array<number | `ellipsis-${number}`> = [];
    sorted.forEach((p, idx) => {
      if (idx > 0 && p - sorted[idx - 1] > 1) items.push(`ellipsis-${sorted[idx - 1]}`);
      items.push(p);
    });
    return items;
  })();
  const getTypeText = (type: string) => {
    if (type === 'student') return 'Schüler';
    if (type === 'customer') return 'Kunde';
    if (type === 'teacher') return 'Lehrer';
    return type;
  };
  const getPersonTypes = (person: Person): PersonListType[] => {
    if (Array.isArray(person.computedTypes) && person.computedTypes.length > 0) {
      return person.computedTypes;
    }
    if (person.type === 'teacher') return ['customer', 'teacher'];
    if (person.type === 'student') return ['student'];
    return ['customer'];
  };
  const toggleCustomerExpanded = async (rootId: string) => {
    const aufklappen = !expandedCustomerIds[rootId];
    setExpandedCustomerIds((prev) => ({ ...prev, [rootId]: aufklappen }));
    // Die zugeordneten Profile werden erst geholt, wenn jemand sie sehen will.
    if (!aufklappen || subprofilesByParent[rootId] !== undefined) return;
    try {
      const liste = await api.get<SubprofileRecord[]>(`/api/personen/${rootId}/subprofiles`);
      setSubprofilesByParent((prev) => ({
        ...prev,
        [rootId]: (Array.isArray(liste) ? liste : []).filter((sub) => Number(sub?.index || 0) > 1),
      }));
    } catch {
      setSubprofilesByParent((prev) => ({ ...prev, [rootId]: [] }));
      toast.error('Zugeordnete Personen konnten nicht geladen werden');
    }
  };
  const handleSaveSubprofile = async (data: Record<string, unknown>) => {
    if (!selectedPerson) return;
    const rootId = getRootPersonId(selectedPerson);
    try {
      const created = await api.post<SubprofileRecord>(`/api/personen/${rootId}/subprofiles`, data);
      if (Number(created?.index || 0) <= 1) {
        toast.success('Schülerprofil gespeichert');
        return;
      }
      setSubprofilesByParent(prev => {
        const existing = prev[rootId] || [];
        return { ...prev, [rootId]: [...existing, created] };
      });
      setNeuLaden((wert) => wert + 1);
      toast.success('Zugeordnete Person erstellt');
    } catch (e) {
      toast.error('Zugeordnete Person konnte nicht erstellt werden');
    } finally {
      setIsSubProfileDialogOpen(false);
      setTimeout(() => setSelectedPerson(null), 100);
    }
  };
  // Teacher-Promotion läuft jetzt über den PersonDetailsWizardModal (Tab "Lehrer")
  return <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Personenverwaltung</h1>
      </div>

      {/* Tabs und Suchleiste */}
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <Tabs
          defaultValue="all"
          value={activeTab}
          onValueChange={(value) => setActiveTab((value as 'all' | PersonListType) || 'all')}
          className="w-full md:w-auto"
        >
          <TabsList>
            <TabsTrigger value="all">Alle</TabsTrigger>
            <TabsTrigger value="student">Schüler</TabsTrigger>
            <TabsTrigger value="customer">Kunde</TabsTrigger>
            <TabsTrigger value="teacher">Lehrer</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input type="search" placeholder="Suchen..." className="pl-8 md:w-[200px] lg:w-[300px]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px]">
              <div className="flex items-center">
                <Filter className="mr-2 h-4 w-4" />
                <span>Status</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              {/* „Ausstehend" konnte nie greifen; „Archiviert" filtert auf das
                  Archiv-Kennzeichen am Datensatz (PERS-6.1). */}
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="inactive">Inaktiv</SelectItem>
              <SelectItem value="archived">Archiviert</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={(value: PeopleSort) => setSortBy(value)}>
            <SelectTrigger className="w-[190px]">
              <SelectValue placeholder="Sortierung" />
            </SelectTrigger>
            <SelectContent>
              {/* „Neueste/Älteste zuerst" liefen ins Leere: die Liste kennt kein
                  Beitrittsdatum. Stattdessen die letzte Aktivität. */}
              <SelectItem value="standard">Aktive zuerst, A–Z</SelectItem>
              <SelectItem value="name_asc">Name A-Z</SelectItem>
              <SelectItem value="name_desc">Name Z-A</SelectItem>
              <SelectItem value="activity_desc">Letzte Aktivität zuerst</SelectItem>
              <SelectItem value="status">Aktive zuerst</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-muted-foreground">
            <span>Alle Standorte</span>
            <Switch checked={alleStandorte} onCheckedChange={setAlleStandorte} className="scale-75" />
          </label>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Neue Person
          </Button>
        </div>
      </div>
      {hasActiveFilters ? (
        <div className="flex flex-wrap items-center gap-2">
          {searchTerm ? (
            <Badge variant="secondary">Suche: "{searchTerm}"</Badge>
          ) : null}
          {activeTab !== 'all' ? (
            <Badge variant="secondary">Typ: {getTypeText(activeTab)}</Badge>
          ) : null}
          {statusFilter !== 'all' ? (
            <Badge variant="secondary">Status: {STATUS_LABEL[statusFilter] || statusFilter}</Badge>
          ) : null}
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Filter zuruecksetzen
          </Button>
        </div>
      ) : null}

      {statusFilter !== 'all' && ausgeblendeteTreffer > 0 ? (
        <p className="text-sm text-muted-foreground">
          {ausgeblendeteTreffer === 1
            ? '1 weiterer Treffer'
            : `${ausgeblendeteTreffer} weitere Treffer`}{' '}
          {statusFilter === 'archived'
            ? 'nicht archiviert'
            : activeTab === 'teacher'
            ? (statusFilter === 'active' ? 'mit inaktiver Lehrer-Rolle' : 'mit aktiver Lehrer-Rolle')
            : (statusFilter === 'active' ? 'ohne laufenden Vertrag' : 'mit laufendem Vertrag')} —{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-foreground"
            onClick={() => setStatusFilter('all')}
          >
            alle anzeigen
          </button>
        </p>
      ) : null}

      {!alleStandorte && ohneStandort > 0 ? (
        <p className="text-sm text-muted-foreground">
          {ohneStandort === 1
            ? '1 Person ohne Standortzuordnung ist ausgeblendet'
            : `${ohneStandort} Personen ohne Standortzuordnung sind ausgeblendet`}{' '}
          — über „Alle Standorte" sichtbar.
        </p>
      ) : null}

      {/* Personen-Grid */}
      {isLoading ? <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div> : pagedPeople.length === 0 ? <div className="rounded-lg border bg-card p-8 text-center">
          <Users className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-semibold">
            {hasActiveFilters ? 'Keine Treffer fuer Ihre Filter' : 'Keine Personen vorhanden'}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {hasActiveFilters ? 'Passe die Filter an oder setze sie zurück.' : 'Lege die erste Person an, um hier zu starten.'}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            {hasActiveFilters ? <Button variant="outline" onClick={clearFilters}>Filter zuruecksetzen</Button> : null}
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Neue Person hinzufuegen
            </Button>
          </div>
        </div> : (
          <div>
            {pagedPeople.map((person, idx) => {
              const currentRootId = getRootPersonId(person);
              const prevPerson = idx > 0 ? pagedPeople[idx - 1] : null;
              const prevRootId = prevPerson ? getRootPersonId(prevPerson) : null;
              const isSubprofileGroupedWithParent = person.isSubprofile && prevRootId === currentRootId;
              const itemSpacingClass = idx === 0 ? '' : (isSubprofileGroupedWithParent ? 'mt-1' : 'mt-3');
              const extraSubprofiles = person.isPrimaryProfile
                ? getLoadedSubprofiles(person)
                : [];
              // Im Kunden-Reiter liefert der Server die Lernenden gleich mit —
              // aufgeklappt, mit Alter und ob gerade Unterricht läuft (T-0042).
              const mitgeliefert = activeTab === 'customer' && person.assignedProfiles
                ? person.assignedProfiles
                : null;
              const customerChildren = mitgeliefert
                ? mitgeliefert.map((profil) => ({
                    id: profil.subprofileId,
                    name: profil.name || 'Schülerprofil',
                    relationshipText: profil.relationshipType === 'self'
                      ? 'eigenes Profil'
                      : (relationshipText(profil.relationshipType || null, person.name) || undefined),
                    subprofileIndex: profil.index,
                    birthDate: profil.birthDate || undefined,
                    aktiv: profil.aktiv === true,
                  }))
                : extraSubprofiles.map((sub) => ({
                    id: String(sub?.id || ''),
                    name: String(sub?.name || `${sub?.firstName || ''} ${sub?.lastName || ''}`.trim() || 'Schülerprofil'),
                    relationshipText: relationshipText(sub?.relationshipType, person.name) || undefined,
                    subprofileIndex: Number(sub?.index || 1),
                  }));
              const rootId = getRootPersonId(person);
              const types = getPersonTypes(person);

              return (
                <div key={person.id} className={itemSpacingClass}>
                  <PersonGridCard
                    person={person}
                    typeBadges={types}
                    managedByName={person.isSubprofile ? person.parentName : undefined}
                    subprofileCount={getSubprofileCount(person)}
                    expandableChildren={customerChildren}
                    isExpanded={mitgeliefert ? expandedCustomerIds[rootId] !== false : !!expandedCustomerIds[rootId]}
                    onToggleExpand={() => {
                      // Mitgelieferte Profile sind vorbelegt aufgeklappt; der Knopf klappt um.
                      if (mitgeliefert) {
                        setExpandedCustomerIds((prev) => ({ ...prev, [rootId]: prev[rootId] === false }));
                        return;
                      }
                      void toggleCustomerExpanded(rootId);
                    }}
                    onOpenChild={(subIndex) => {
                      navigate(subprofileDetailPath(rootId, subIndex, location.pathname));
                    }}
                    onEdit={() => {
                      if (activeTab === 'student') {
                        navigate(subprofileDetailPath(rootId, person.subprofileIndex || 1, location.pathname));
                        return;
                      }
                      if (person.isSubprofile) {
                        navigate(subprofileDetailPath(rootId, person.subprofileIndex || 1, location.pathname));
                        return;
                      }
                      setDetailsPersonId(rootId);
                      setDetailsOpen(true);
                    }}
                    onOpenDeepLink={() => {
                      if (activeTab === 'student') {
                        navigate(subprofileDetailPath(rootId, person.subprofileIndex || 1, location.pathname));
                        return;
                      }
                      if (person.isSubprofile) {
                        navigate(subprofileDetailPath(rootId, person.subprofileIndex || 1, location.pathname));
                        return;
                      }
                      navigate(personDetailPath(rootId, location.pathname));
                    }}
                    onEmail={() => {
                      const standort = selectedStandortId ? Number(selectedStandortId) : 0;
                      if (!standort) {
                        toast.error('Kein Standort ausgewählt');
                        return;
                      }
                      const wpUserId = Number.parseInt(String(person.wpUserId || person.id || ''), 10);
                      if (!wpUserId || Number.isNaN(wpUserId)) {
                        toast.error('Ungültige Person-ID');
                        return;
                      }
                      setMessageRecipients([
                        {
                          id: `person:${wpUserId}`,
                          type: 'person',
                          wpUserId,
                          name: person.name,
                          email: person.email || null,
                          phone: resolvePreferredPhone(person),
                        },
                      ]);
                      setMessageOpen(true);
                    }}
                  />
                </div>
              );
            })}

            <div className="flex items-center justify-between border-t pt-3">
              <div className="text-sm text-muted-foreground">
                {totalFiltered === 0 ? (
                  <>Zeige 0 Personen</>
                ) : (
                  <>
                    Zeige {pageStartIdx + 1}–{pageEndIdx} von {totalFiltered} Personen
                  </>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageEndIdx >= totalFiltered}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Weiter
                </Button>
              </div>
              <div className="hidden md:flex items-center gap-1">
                {compactPageButtons.map((item) =>
                  typeof item === 'string' ? (
                    <span key={item} className="px-1 text-sm text-muted-foreground">...</span>
                  ) : (
                    <Button
                      key={item}
                      size="sm"
                      variant={item === page ? 'default' : 'outline'}
                      onClick={() => setPage(item)}
                    >
                      {item + 1}
                    </Button>
                  )
                )}
              </div>
              <div className="hidden md:flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={jumpToPage}
                  onChange={(e) => setJumpToPage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      goToPage();
                    }
                  }}
                  placeholder="Seite"
                  className="h-8 w-20"
                />
                <Button size="sm" variant="outline" onClick={goToPage}>
                  Gehe
                </Button>
              </div>
            </div>
          </div>
        )}

      {/* Person details/edit wizard */}
      <PersonDetailsWizardModal
        open={detailsOpen}
        onOpenChange={(v) => {
          setDetailsOpen(v);
          if (!v) {
            setTimeout(() => setDetailsPersonId(null), 150);
          }
        }}
        personId={detailsPersonId}
        onUpdated={(updated) => {
          setPeople((prev) => prev.map((p) => {
            const rootId = getRootPersonId(p);
            if (rootId !== String(updated.id)) return p;
            return {
              ...p,
              email: updated.email || p.email,
              phone: updated.phone || p.phone,
              communicationChannels: Array.isArray((updated as Person).communicationChannels)
                ? (updated as Person).communicationChannels
                : p.communicationChannels,
              roles: Array.isArray((updated as Person).roles) ? (updated as Person).roles : p.roles,
              computedTypes: p.isPrimaryProfile
                ? (((updated as Person).roles || p.roles || []).includes('teacher') ? ['customer', 'teacher'] : ['customer'])
                : ['student'],
            } as Person;
          }));
        }}
        onDeleted={(id) => {
          setPeople((prev) => prev.filter((p) => getRootPersonId(p) !== String(id)));
          setSubprofilesByParent((prev) => {
            const next = { ...prev };
            delete next[String(id)];
            return next;
          });
        }}
      />

      <MessageWizardModal
        open={messageOpen}
        onOpenChange={setMessageOpen}
        standortId={selectedStandortId ? Number(selectedStandortId) : 0}
        title="Nachricht an Person"
        recipients={messageRecipients}
        initialChannel="email"
        templateKeys={{
          email: 'communications.manual.person',
          sms: 'communications.manual.person',
        }}
        initialDrafts={{
          email: {
            subjectTpl: 'Nachricht von {{schoolName}}',
            htmlTpl: '<p>Hallo {{recipientName}},</p><p></p><p>Viele Grüße<br>{{schoolName}}</p>',
            textTpl: 'Hallo {{recipientName}},\n\n\nViele Grüße\n{{schoolName}}',
          },
          sms: { smsTpl: 'Hallo {{recipientName}}, ' },
        }}
      />

      {/* Add sub-profile wizard (WizardShell via SubProfileForm) */}
      <SubProfileForm
        parent={selectedPerson as Person}
        isOpen={isSubProfileDialogOpen}
        onClose={() => {
          setIsSubProfileDialogOpen(false);
          setTimeout(() => setSelectedPerson(null), 100);
        }}
        onSave={handleSaveSubprofile}
      />

      {/* Create person dialog */}
      <WizardShell
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        title="Neue Person anlegen"
        contentClassName="w-[1440px] h-[1080px] max-w-[95vw] max-h-[95vh]"
        bodyClassName="py-4"
        onCancel={() => setIsCreateDialogOpen(false)}
        cancelLabel="Abbrechen"
        onPrimary={() => handleCreatePerson()}
        primaryLabel="Erstellen"
        canPrimary={Boolean(createForm.email.trim() && (createForm.firstName.trim() || createForm.lastName.trim()))}
        isLoading={isLoading}
      >
          <div className="grid gap-4">
            <div className="flex items-center gap-2">
              <input id="isCompanyCreate" type="checkbox" className="h-4 w-4 rounded border-gray-300" checked={createForm.isCompany} onChange={e => setCreateForm(prev => ({ ...prev, isCompany: e.target.checked, companyName: e.target.checked ? prev.companyName : '' }))} />
              <label htmlFor="isCompanyCreate" className="text-sm font-medium">Firma</label>
            </div>
            {createForm.isCompany && (
              <div>
                <label htmlFor="companyNameCreate" className="text-sm font-medium">Firmenname</label>
                <Input id="companyNameCreate" className="mt-1" value={createForm.companyName} onChange={e => setCreateForm(prev => ({ ...prev, companyName: e.target.value }))} placeholder="Firmenname eingeben" />
              </div>
            )}
            <div>
              <label htmlFor="salutation" className="text-sm font-medium">Anrede</label>
              <Select value={createForm.salutation} onValueChange={(val) => setCreateForm(prev => ({ ...prev, salutation: val as CreateSalutation }))}>
                <SelectTrigger id="salutation" className="mt-1">
                  <span>{createForm.salutation || 'Anrede auswählen'}</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Herr">Herr</SelectItem>
                  <SelectItem value="Frau">Frau</SelectItem>
                  <SelectItem value="Divers">Divers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="text-sm font-medium">Vorname</label>
                <Input id="firstName" className="mt-1" value={createForm.firstName} onChange={e => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))} />
              </div>
              <div>
                <label htmlFor="lastName" className="text-sm font-medium">Nachname</label>
                <Input id="lastName" className="mt-1" value={createForm.lastName} onChange={e => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))} />
              </div>
            </div>
            <div>
              <label htmlFor="emailCreate" className="text-sm font-medium">E-Mail</label>
              <Input id="emailCreate" className="mt-1" type="email" value={createForm.email} onChange={e => { setEmailDublette(null); setCreateForm(prev => ({ ...prev, email: e.target.value })); }} />
              {emailDublette && (
                <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
                  <p>
                    Diese E-Mail gehört schon zu{' '}
                    <a
                      href={`/dashboard/people/${emailDublette.personId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium underline"
                    >
                      {emailDublette.name}
                    </a>
                    . Meist ist das dieselbe Person — dann bitte dort weiterarbeiten.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    disabled={isLoading}
                    onClick={() => handleCreatePerson(true)}
                  >
                    Trotzdem neu anlegen
                  </Button>
                </div>
              )}
            </div>
            <div>
              <label htmlFor="birthDateCreate" className="text-sm font-medium">Geburtsdatum</label>
              <Input id="birthDateCreate" className="mt-1" type="date" value={createForm.birthDate} onChange={e => setCreateForm(prev => ({ ...prev, birthDate: e.target.value }))} />
            </div>
            <div className="relative">
              <label htmlFor="addressCreate" className="text-sm font-medium">Adresse</label>
              <Input id="addressCreate" className="mt-1" placeholder="Adresse suchen..." autoComplete="off" name="person-create-address-search" value={addressCreateQuery || createForm.address} onChange={e => {
                setAddressCreateQuery(e.target.value);
                setCreateForm(prev => ({
                  ...prev,
                  address: e.target.value,
                  structuredAddress: {
                    ...(prev.structuredAddress || {}),
                    formatted: e.target.value,
                  },
                }));
              }} />
              {addressCreateSuggestions.length > 0 && <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow">
                  {addressCreateSuggestions.map(s => <button key={String(s['id'] ?? `${s['label'] ?? 'address'}`)} type="button" className="w-full text-left px-3 py-2 hover:bg-muted" onClick={() => {
                    const structured = (s['structured'] as StructuredAddress | undefined) || {};
                    const zip = String(structured.zip || structured.postalCode || '').trim();
                    setCreateForm(prev => ({
                      ...prev,
                      address: String(structured.formatted || ''),
                      structuredAddress: {
                        ...structured,
                        zip,
                        postalCode: zip,
                      },
                    }));
                    setAddressCreateQuery(String(structured.formatted || ''));
                    setAddressCreateSuggestions([]);
                  }}>
                      {String(s['label'] ?? structured.formatted ?? '')}
                    </button>)}
                </div>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input
                placeholder="Straße"
                value={String(createForm.structuredAddress?.street || '')}
                onChange={(e) => setCreateForm((prev) => {
                  const current: StructuredAddress = prev.structuredAddress || {};
                  const next: StructuredAddress = { ...current, street: e.target.value };
                  const line1 = `${next.street || ''} ${next.houseNumber || ''}`.trim();
                  const line2 = `${next.zip || next.postalCode || ''} ${next.city || ''}`.trim();
                  next.formatted = [line1, line2].filter(Boolean).join(', ');
                  return { ...prev, structuredAddress: next, address: next.formatted || prev.address };
                })}
              />
              <Input
                placeholder="PLZ"
                value={String(createForm.structuredAddress?.zip || createForm.structuredAddress?.postalCode || '')}
                onChange={(e) => setCreateForm((prev) => {
                  const current: StructuredAddress = prev.structuredAddress || {};
                  const next: StructuredAddress = { ...current, zip: e.target.value, postalCode: e.target.value };
                  const line1 = `${next.street || ''} ${next.houseNumber || ''}`.trim();
                  const line2 = `${next.zip || next.postalCode || ''} ${next.city || ''}`.trim();
                  next.formatted = [line1, line2].filter(Boolean).join(', ');
                  return { ...prev, structuredAddress: next, address: next.formatted || prev.address };
                })}
              />
              <Input
                placeholder="Ort"
                value={String(createForm.structuredAddress?.city || '')}
                onChange={(e) => setCreateForm((prev) => {
                  const current: StructuredAddress = prev.structuredAddress || {};
                  const next: StructuredAddress = { ...current, city: e.target.value };
                  const line1 = `${next.street || ''} ${next.houseNumber || ''}`.trim();
                  const line2 = `${next.zip || next.postalCode || ''} ${next.city || ''}`.trim();
                  next.formatted = [line1, line2].filter(Boolean).join(', ');
                  return { ...prev, structuredAddress: next, address: next.formatted || prev.address };
                })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input id="newsletterCreate" type="checkbox" className="h-4 w-4 rounded border-gray-300" checked={createForm.newsletter} onChange={e => setCreateForm(prev => ({ ...prev, newsletter: e.target.checked }))} />
              <label htmlFor="newsletterCreate" className="text-sm">Newsletter erhalten</label>
            </div>
            {/* Kommunikation */}
            {(createForm.communications || []).map((c, idx) => (
              <div key={idx} className="grid gap-2 md:grid-cols-6 items-center">
                <div className="md:col-span-2">
                  <Select value={c.type} onValueChange={(val) => setCreateForm(prev => {
                    const list = [...prev.communications];
                    list[idx] = { ...list[idx], type: val };
                    return { ...prev, communications: list };
                  })}>
                    <SelectTrigger>
                      {/* Die Bezeichnung, nicht der Schlüssel — „Mobil“ statt „mobil“ (T-0261). */}
                      <span>{KONTAKTWEG_LABEL[c.type] || c.type || 'Typ'}</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">E-Mail</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="mobil">Mobil</SelectItem>
                      <SelectItem value="festnetz">Festnetz</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <Input placeholder="Wert" value={c.value} onChange={e => setCreateForm(prev => {
                    const list = [...prev.communications];
                    list[idx] = { ...list[idx], value: e.target.value };
                    return { ...prev, communications: list };
                  })} />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <Button type="button" variant="outline" className="text-destructive" onClick={() => setCreateForm(prev => ({ ...prev, communications: prev.communications.filter((_, i) => i !== idx) }))}>Entfernen</Button>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={() => setCreateForm(prev => ({ ...prev, communications: [...prev.communications, { type: 'mobil', value: '' }] }))}>Kommunikation hinzufügen</Button>
          </div>
      </WizardShell>

    </div>;
};
export default PersonManagement;
